import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { FAQ_GROUPS } from "@/data/content";
import { useEnroll } from "@/context/EnrollContext";
import { Sparkles, Bot, ArrowRight, ShieldCheck, Zap, MessageSquare, Settings } from "lucide-react";

const GROUP_ICONS = {
  "About CHINI": Bot,
  "What CHINI Can Help You With": MessageSquare,
  "Privacy & Data": ShieldCheck,
  "Getting Started": Zap,
};

const GROUP_COLORS = {
  "About CHINI": "from-orange-500/20 to-orange-500/5",
  "What CHINI Can Help You With": "from-blue-500/20 to-blue-500/5",
  "Privacy & Data": "from-green-500/20 to-green-500/5",
  "Getting Started": "from-purple-500/20 to-purple-500/5",
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: [0.4, 0, 0.2, 1] },
  }),
};

const FaqAnswer = ({ faq }) => (
  <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
    {faq.a && <p>{faq.a}</p>}
    {faq.list && (
      <ul className="mt-2 space-y-1.5">
        {faq.list.map((item, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
            {item}
          </li>
        ))}
      </ul>
    )}
    {faq.link && (
      <p className="mt-2">
        <a
          href={faq.link.href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 font-semibold text-primary underline underline-offset-2 hover:text-primary/80 transition-colors"
        >
          {faq.link.text} <ArrowRight className="h-3 w-3" />
        </a>
      </p>
    )}
  </div>
);

export const Faq = () => {
  const { openEnroll } = useEnroll();
  let globalIndex = 0;

  return (
    <section id="faq" className="relative overflow-hidden bg-secondary/30 py-20 lg:py-28">
      {/* Subtle bg texture */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent" />

      <div className="relative mx-auto max-w-5xl px-5 lg:px-8">

        {/* Header */}
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="mx-auto max-w-2xl text-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-primary">
            <Sparkles className="h-3.5 w-3.5" /> FAQ
          </span>
          <h2 className="section-title mt-4 text-foreground">
            CHINI, Your Hybrid AI{" "}
            <span className="text-gradient-primary">Interview Coach</span>
          </h2>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            Everything you need to know about CHINI. Still have questions?{" "}
            <a href="https://changeurstory.in" target="_blank" rel="noopener noreferrer" className="whitespace-nowrap font-semibold text-primary underline underline-offset-2">
              Visit changeurstory.in
            </a>
          </p>
        </motion.div>

        {/* Group tabs overview */}
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1} variants={fadeUp}
          className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4"
        >
          {FAQ_GROUPS.map((group) => {
            const Icon = GROUP_ICONS[group.group] || Settings;
            const gradient = GROUP_COLORS[group.group] || "from-primary/20 to-primary/5";
            return (
              <a
                key={group.group}
                href={`#faq-group-${group.group.replace(/\s+/g, "-")}`}
                className={`flex flex-col items-center gap-2 rounded-2xl bg-gradient-to-b ${gradient} border border-border p-4 text-center text-xs font-semibold text-foreground transition-all hover:-translate-y-0.5 hover:shadow-sm`}
              >
                <Icon className="h-5 w-5 text-primary" />
                {group.group}
              </a>
            );
          })}
        </motion.div>

        {/* FAQ Groups */}
        <div className="mt-14 space-y-12">
          {FAQ_GROUPS.map((group, gi) => {
            const Icon = GROUP_ICONS[group.group] || Settings;
            return (
              <motion.div
                key={group.group}
                id={`faq-group-${group.group.replace(/\s+/g, "-")}`}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                custom={gi}
                variants={fadeUp}
              >
                {/* Group heading */}
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="font-display text-xl font-bold text-foreground">{group.group}</h3>
                  <div className="h-px flex-1 bg-border" />
                  <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                    {group.faqs.length} Q
                  </span>
                </div>

                <Accordion type="single" collapsible className="space-y-2.5">
                  {group.faqs.map((faq, fi) => {
                    const idx = globalIndex++;
                    return (
                      <AccordionItem
                        key={fi}
                        value={`faq-${gi}-${fi}`}
                        className="overflow-hidden rounded-2xl border border-border bg-white px-6 shadow-sm transition-all hover:border-primary/30"
                      >
                        <AccordionTrigger className="py-5 text-left font-display text-base font-semibold text-foreground hover:no-underline">
                          <span className="flex items-start gap-3">
                            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">
                              {idx + 1}
                            </span>
                            {faq.q}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="pb-5 pl-8">
                          <FaqAnswer faq={faq} />
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true }} custom={5} variants={fadeUp}
          className="mt-16 overflow-hidden rounded-3xl bg-gradient-to-r from-[#1a1a2e] to-[#0f3460] p-10 text-center"
        >
          <Sparkles className="mx-auto h-8 w-8 text-primary" />
          <h3 className="mt-4 font-display text-2xl font-bold text-white sm:text-3xl">
            Ready to Start Practising?
          </h3>
          <p className="mt-3 text-sm text-white/60">
            Free, instant, and unlimited. Your next offer begins with the next question you ask.
          </p>
          <Button
            variant="hero"
            size="lg"
            onClick={() => openEnroll("Professional")}
            className="group mt-7 px-10 text-base shadow-lg shadow-primary/30"
          >
            Try CHINI Now
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Button>
        </motion.div>

      </div>
    </section>
  );
};
